import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, NavLink } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  LayoutDashboard, 
  Users,
  Heart,
  Activity,
  Coins,
  LogOut,
  Plus,
  Trash2,
  TrendingUp,
  Building2,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

// Dashboard Layout Component
function DashboardLayout({ children, auth }) {
  const navigate = useNavigate();
  const { user, logout } = auth;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { to: '/company', icon: LayoutDashboard, label: 'Vue d\'ensemble', end: true },
    { to: '/company/employees', icon: Users, label: 'Salariés' },
    { to: '/company/campaigns', icon: Heart, label: 'Campagnes' },
    { to: '/company/rse', icon: Activity, label: 'Actions RSE' },
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-stone-200 flex flex-col fixed h-full">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-800 rounded-xl flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                InvesThune
              </span>
              <p className="text-xs text-emerald-600 font-medium">Entreprise</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-100">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="text-emerald-800 font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.company_name || user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
}

// Overview Page
function OverviewPage({ auth }) {
  const [employees, setEmployees] = useState([]);
  const [rseActions, setRseActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, user } = auth;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const [empRes, rseRes] = await Promise.all([
          fetch(`${API}/employees`, { credentials: 'include', headers }),
          fetch(`${API}/rse-actions`, { credentials: 'include', headers })
        ]);
        
        if (empRes.ok) setEmployees(await empRes.json());
        if (rseRes.ok) setRseActions(await rseRes.json());
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const totalRounding = employees.reduce((sum, emp) => sum + (emp.rounding_amount || 0), 0);
  const totalRSE = rseActions.reduce((sum, action) => sum + action.amount, 0);
  const totalDonated = employees.reduce((sum, emp) => sum + (emp.total_donated || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="company-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Bienvenue, {user?.company_name || user?.name}
        </h1>
        <p className="text-slate-600">Tableau de bord de votre entreprise</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Salariés actifs</p>
                <p className="text-3xl font-bold text-slate-800 counter">{employees.length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Prélèvement mensuel</p>
                <p className="text-3xl font-bold text-emerald-700 counter">{totalRounding.toFixed(2)}€</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total donné</p>
                <p className="text-3xl font-bold text-orange-600 counter">{totalDonated.toFixed(2)}€</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Actions RSE</p>
                <p className="text-3xl font-bold text-slate-800 counter">{totalRSE.toFixed(2)}€</p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Employees */}
      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Salariés récents
          </CardTitle>
          <NavLink to="/company/employees">
            <Button variant="outline" size="sm" className="rounded-full">
              Voir tout
            </Button>
          </NavLink>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun salarié enregistré</p>
              <NavLink to="/company/employees">
                <Button className="mt-4 bg-emerald-800 hover:bg-emerald-900 rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un salarié
                </Button>
              </NavLink>
            </div>
          ) : (
            <div className="space-y-3">
              {employees.slice(0, 5).map((emp) => (
                <div key={emp.employee_id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="text-emerald-800 font-semibold">{emp.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{emp.name}</p>
                      <p className="text-sm text-slate-500">{emp.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-emerald-700">{emp.rounding_amount?.toFixed(2)}€/mois</p>
                    <p className="text-sm text-slate-500">Total: {emp.total_donated?.toFixed(2)}€</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Employees Page
function EmployeesPage({ auth }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    salary: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { token } = auth;

  const fetchEmployees = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API}/employees`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        setEmployees(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/employees`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          salary: parseFloat(formData.salary)
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'ajout');
      }

      toast.success('Salarié ajouté avec succès');
      setIsOpen(false);
      setFormData({ name: '', email: '', salary: '' });
      fetchEmployees();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (employeeId) => {
    if (!confirm('Supprimer ce salarié ?')) return;

    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/employees/${employeeId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      toast.success('Salarié supprimé');
      fetchEmployees();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Calculate rounding preview
  const calculateRounding = (salary) => {
    if (!salary) return 0;
    const sal = parseFloat(salary);
    const decimal = sal - Math.floor(sal);
    return decimal > 0 ? (1 - decimal + 1).toFixed(2) : 1;
  };

  return (
    <div data-testid="employees-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestion des salariés
          </h1>
          <p className="text-slate-600">Gérez les salariés participants au programme</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-800 hover:bg-emerald-900 rounded-full" data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un salarié
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouveau salarié</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom complet</Label>
                <Input
                  placeholder="Jean Dupont"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="jean@exemple.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="employee-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Salaire mensuel (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="2500.45"
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  required
                  data-testid="employee-salary-input"
                />
                {formData.salary && (
                  <p className="text-sm text-emerald-600">
                    Prélèvement mensuel : {calculateRounding(formData.salary)}€ (1€ + arrondi)
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-emerald-800 hover:bg-emerald-900"
                  disabled={submitting}
                  data-testid="submit-employee-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun salarié enregistré</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Salarié</th>
                  <th>Email</th>
                  <th>Salaire</th>
                  <th>Prélèvement/mois</th>
                  <th>Total donné</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.employee_id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-800 text-sm font-semibold">{emp.name.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-slate-800">{emp.name}</span>
                      </div>
                    </td>
                    <td className="text-slate-600">{emp.email}</td>
                    <td className="font-mono">{emp.salary?.toFixed(2)}€</td>
                    <td className="font-semibold text-emerald-700">{emp.rounding_amount?.toFixed(2)}€</td>
                    <td className="text-orange-600">{emp.total_donated?.toFixed(2)}€</td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(emp.employee_id)}
                        data-testid={`delete-employee-${emp.employee_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Campaigns Page (for company to see available campaigns)
function CampaignsPage({ auth }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = auth;

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await fetch(`${API}/campaigns/all`);
        if (response.ok) {
          setCampaigns(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch campaigns:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  return (
    <div data-testid="campaigns-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Campagnes de collecte
        </h1>
        <p className="text-slate-600">Découvrez les campagnes actives des associations</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardContent className="text-center py-12">
            <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune campagne active pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => {
            const progress = (campaign.current_amount / campaign.goal_amount) * 100;
            return (
              <Card key={campaign.campaign_id} className="bg-white border-stone-200 rounded-2xl overflow-hidden card-hover">
                {campaign.image_url && (
                  <img 
                    src={campaign.image_url} 
                    alt={campaign.title}
                    className="w-full h-40 object-cover"
                  />
                )}
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {campaign.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{campaign.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Collecté</span>
                      <span className="font-semibold text-emerald-700">
                        {campaign.current_amount.toFixed(2)}€ / {campaign.goal_amount.toFixed(2)}€
                      </span>
                    </div>
                    <Progress value={Math.min(progress, 100)} className="h-2" />
                  </div>

                  <NavLink to={`/company/rse?campaign=${campaign.campaign_id}`}>
                    <Button className="w-full bg-orange-600 hover:bg-orange-700 rounded-full">
                      <Heart className="w-4 h-4 mr-2" />
                      Soutenir
                    </Button>
                  </NavLink>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// RSE Actions Page
function RSEPage({ auth }) {
  const [rseActions, setRseActions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    campaign_id: '',
    amount: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { token } = auth;

  const fetchData = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const [rseRes, campRes] = await Promise.all([
        fetch(`${API}/rse-actions`, { credentials: 'include', headers }),
        fetch(`${API}/campaigns/all`)
      ]);

      if (rseRes.ok) setRseActions(await rseRes.json());
      if (campRes.ok) setCampaigns(await campRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/rse-actions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount)
        }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de la création');

      toast.success('Action RSE enregistrée');
      setIsOpen(false);
      setFormData({ campaign_id: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const totalRSE = rseActions.reduce((sum, a) => sum + a.amount, 0);

  return (
    <div data-testid="rse-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Actions RSE
          </h1>
          <p className="text-slate-600">Dons directs de l'entreprise aux associations</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-800 hover:bg-emerald-900 rounded-full" data-testid="add-rse-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle action RSE
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouvelle action RSE</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Campagne</Label>
                <select
                  className="w-full p-3 bg-stone-50 border border-stone-200 rounded-lg"
                  value={formData.campaign_id}
                  onChange={(e) => setFormData({ ...formData, campaign_id: e.target.value })}
                  required
                  data-testid="rse-campaign-select"
                >
                  <option value="">Sélectionner une campagne</option>
                  {campaigns.map((c) => (
                    <option key={c.campaign_id} value={c.campaign_id}>{c.title}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Montant (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="500.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  data-testid="rse-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Input
                  placeholder="Don de fin d'année..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-emerald-800 hover:bg-emerald-900"
                  disabled={submitting}
                  data-testid="submit-rse-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="stat-card">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Total RSE</p>
            <p className="text-3xl font-bold text-emerald-700 counter">{totalRSE.toFixed(2)}€</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Nombre d'actions</p>
            <p className="text-3xl font-bold text-slate-800 counter">{rseActions.length}</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Campagnes soutenues</p>
            <p className="text-3xl font-bold text-orange-600 counter">
              {new Set(rseActions.map(a => a.campaign_id)).size}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : rseActions.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune action RSE enregistrée</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Campagne</th>
                  <th>Montant</th>
                  <th>Description</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {rseActions.map((action) => (
                  <tr key={action.action_id}>
                    <td className="font-mono text-sm text-slate-600">{action.action_id}</td>
                    <td className="text-slate-800">{action.campaign_id}</td>
                    <td className="font-semibold text-emerald-700">{action.amount.toFixed(2)}€</td>
                    <td className="text-slate-600">{action.description || '-'}</td>
                    <td className="text-slate-500">
                      {new Date(action.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Main Company Dashboard Component
export default function CompanyDashboard({ auth }) {
  return (
    <DashboardLayout auth={auth}>
      <Routes>
        <Route index element={<OverviewPage auth={auth} />} />
        <Route path="employees" element={<EmployeesPage auth={auth} />} />
        <Route path="campaigns" element={<CampaignsPage auth={auth} />} />
        <Route path="rse" element={<RSEPage auth={auth} />} />
      </Routes>
    </DashboardLayout>
  );
}
